import { useMemo } from "react";
import {
  selectFavouriteRecipes,
  selectRecipeViewMode,
  selectLastCookedByRecipe,
  selectCookCountByRecipe,
  selectRecipeSort,
  setRecipeViewMode,
  setRecipeSort,
  splitByCooked,
  useT,
  type RecipeSortOption,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";
import { RecipeGrid } from "../components/RecipeGrid";
import { RecipeList } from "../components/RecipeList";
import { RecipeFilterBar, useRecipeFilter } from "../components/RecipeBarFilter";
import { RecipeViewSwitcher } from "../components/RecipeViewSwitcher";
import { RecipeSort } from "../components/RecipeSort";

export default function Favourites() {
  const t = useT();
  const dispatch = useAppDispatch();
  const favourites = useAppSelector(selectFavouriteRecipes);
  const viewMode = useAppSelector(selectRecipeViewMode);
  const lastCookedAt = useAppSelector(selectLastCookedByRecipe);
  const cookCounts = useAppSelector(selectCookCountByRecipe);

  // Filter state + filtered list (owned by the hook; resets on unmount).
  const { filters, activeKeys, toggle, clear, filteredRecipes } =
    useRecipeFilter(favourites);
  // Shared sort preference (library/favourites/quick), persisted per device.
  const sort = useAppSelector(selectRecipeSort) as RecipeSortOption;
  const setSort = (option: RecipeSortOption) => dispatch(setRecipeSort(option));

  // Cooked-only sorts (e.g. "Most cooked") split into a cooked group in the sort's
  // order plus a never-cooked remainder shown under a "Never cooked" divider, so
  // favourites that haven't been cooked aren't hidden. Any other sort puts
  // everything in `cooked`. Mirrors the main library.
  const { cooked, neverCooked } = useMemo(
    () => splitByCooked(filteredRecipes, sort, lastCookedAt, cookCounts),
    [filteredRecipes, sort, lastCookedAt, cookCounts],
  );
  const total = cooked.length + neverCooked.length;
  // Show the divider only on a genuine mix; with nothing cooked yet, show all as
  // one group (no lone heading).
  const showNeverCooked = cooked.length > 0 && neverCooked.length > 0;
  const emptyMessage = t("favourites.emptyFiltered");

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="font-display text-3xl font-extrabold text-ink">{t("nav.favourites")}</h1>
        {favourites.length > 0 && (
          <div className="flex items-center gap-2">
            <RecipeSort value={sort} onChange={setSort} />
            <RecipeViewSwitcher
              viewMode={viewMode}
              onSwitch={(mode) => dispatch(setRecipeViewMode(mode))}
            />
          </div>
        )}
      </div>

      {favourites.length === 0 ? (
        <p className="text-ink-faint text-sm">{t("favourites.emptyHint")}</p>
      ) : (
        <>
          <RecipeFilterBar
            filters={filters}
            activeKeys={activeKeys}
            onToggle={toggle}
            onClear={clear}
            totalCount={favourites.length}
          />

          <p className="text-sm text-ink-faint font-medium mb-4">
            {t("favourites.recipeCount", { count: total })}
          </p>

          {total === 0 ? (
            viewMode === "grid" ? (
              <RecipeGrid recipes={[]} emptyMessage={emptyMessage} />
            ) : (
              <RecipeList recipes={[]} emptyMessage={emptyMessage} />
            )
          ) : (
            <>
              {viewMode === "grid" ? (
                <RecipeGrid recipes={cooked.length ? cooked : neverCooked} />
              ) : (
                <RecipeList recipes={cooked.length ? cooked : neverCooked} />
              )}

              {showNeverCooked && (
                <>
                  <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-faint mt-10 mb-3">
                    {t("library.neverCooked")}
                  </h2>
                  {viewMode === "grid" ? (
                    <RecipeGrid recipes={neverCooked} />
                  ) : (
                    <RecipeList recipes={neverCooked} />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
